<template lang="pug">
el-drawer(
  title="個案簡歷"
  :visible.sync="drawer"
  direction="rtl"
  :before-close="handleClose"
  :append-to-body="appendToBody")
  .drawer.flex.column.gap-20.default
    .name-block.flex.column.gap-10
      .name-row.flex.align-center.justify-space-between
      .name.flex.align-center.gap-10
        span {{data.family_name}}{{data.given_name}}
        el-button(
          v-if="!hideEdit"
          icon="el-icon-edit-outline", round, size="mini"
          @click="editPerson")
        .status.flex.row.gap-10
          el-tooltip(
            effect="dark"
            placement="top"
            :content="!data.verified ? 'Email 未驗證' : 'Email 已驗證'"
          )
            i.el-icon-message(:class="{'warn-color': !data.verified, 'success-color': data.verified}")
          el-tooltip(
            effect="dark"
            placement="top"
            :content="data.line_token ? 'LINE 帳號已綁定' : 'LINE 帳號未綁定'"
          )
            .line-img(:class="{'invalid': !data.line_token}")
      .id 個案編號: {{ data.id }}
      .operation.flex.gap-10(v-if="!hideDetail || !hideOpenChat || showOpenRecord")
        el-button(
          v-if="showOpenRecord"
          type="primary", plain, size="small"
          @click="showPersonRecord") 查看紀錄
        el-button(
          v-if="!hideDetail"
          type="primary", plain, size="small"
          @click="showDetail") 查看詳細資訊
        line-chat-id-edit-button(v-if="canEditLineChat", :id="data.line_chat_id", :uid="data.id")
    .seperator
    .info-block.flex.column.gap-10
      .info-column.flex.column.align-start(
        v-for="col in columns"
        v-if="!(simpleMode && col.advanceOnly)"
      )
        .colname {{ col.text }}
        template(v-if="col.converter")
          .value {{ col.converter(data[col.key]) }}
        template(v-else)
          .value {{ data[col.key] || '-' }}
      .info-column.flex.column.align-start(v-if="!simpleMode")
        .colname 地址
        .value {{ data.country }}
        .value {{ data.county }}{{ data.district }}
        .value {{ data.address }}
      .info-column.flex.column.align-start
        .colname.danger-dark-color 緊急聯絡人
        template(v-if="data.emergency_person && data.emergency_person !== ''")
          .value {{ data.emergency_person }} ({{ data.emergency_relationship }})
          .value {{ $getShowPhone(data.emergency_contact) }}
        template(v-else)
          .value -
      .info-column.flex.column.align-start(
        v-for="col in additionalColumns"
        v-if="!(simpleMode && col.advanceOnly)"
      )
        .colname {{ col.text }}
        template(v-if="col.converter")
          .value {{ col.converter(data[col.key]) }}
        template(v-else)
          .value {{ data[col.key] || '-' }}
  person-editor(
    :pro-edit="proEdit"
    @finish="updateResult"
    ref="personEditor", :append-to-body="true")
</template>

<script>
import PersonEditor from '@/components/drawers/PersonEdit.vue';
import { getUser } from '@/api/user';
import { getPatient } from '@/api/patient';

export default {
  name: 'person-viewer',
  props: {
    appendToBody: {
      type: Boolean,
      default: false,
    },
    hideDetail: {
      type: Boolean,
      default: false,
    },
    hideEdit: {
      type: Boolean,
      default: false,
    },
    hideOpenChat: {
      type: Boolean,
      default: false,
    },
    showOpenRecord: {
      type: Boolean,
      default: false,
    },
    simpleMode: {
      type: Boolean,
      default: false,
    },
    patientMode: {
      type: Boolean,
      default: false,
    },
    proEdit: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    'person-editor': PersonEditor,
  },
  data() {
    return {
      drawer: false,
      data: {},
      columns: [
        {
          text: '生理性別',
          key: 'gender',
          converter: (key) => ({
            male: '男性',
            female: '女性',
            other: '其他',
          }[key] || '-'),
        },
        {
          text: '稱謂',
          key: 'wanna_be_called',
        },
        {
          text: '出生年月日',
          key: 'birthday',
          converter: (day) => {
            if (day && day.indexOf && day.indexOf('-') >= 0) {
              return day;
            }
            return this.$timestampToDate(day) || '-';
          },
        },
        {
          text: '身分證字號',
          key: 'id_number',
        },
        {
          text: '護照號碼',
          key: 'passport',
        },
        {
          text: '聯絡電話',
          key: 'phone',
          advanceOnly: true,
          converter: (phone) => this.$getShowPhone(phone),
        },
        {
          text: 'Email',
          key: 'email',
          advanceOnly: true,
        },
        {
          text: '感情狀態',
          key: 'relationship',
          converter: (key) => this.$tryMsg(`appointment.relationship.${key}`) || '未知',
        },
        {
          text: '最高學歷',
          key: 'education',
          converter: (key) => this.$tryMsg(`appointment.education.${key}`) || '未知',
        },
        {
          text: '產業',
          key: 'industry',
        },
        {
          text: '職業',
          key: 'job',
        },
        {
          text: '職務狀態',
          key: 'job_position',
        },
        {
          text: '工作年資',
          key: 'job_tenure',
        },
      ],
      additionalColumns: [
        {
          text: '語言',
          key: 'prefer_language',
          advanceOnly: true,
        },
        {
          text: '案件來源',
          key: 'refer_source',
          advanceOnly: true,
        },
        {
          text: '就醫紀錄/諮商經驗',
          key: 'therapy_history',
          advanceOnly: true,
        },
        {
          text: '是否嘗試過自殺',
          key: 'suicide_record',
          advanceOnly: true,
        },
        {
          text: '集團',
          key: 'corp',
          advanceOnly: true,
        },
        {
          text: '公司',
          key: 'company',
          advanceOnly: true,
        },
        {
          text: '單位',
          key: 'department',
          advanceOnly: true,
        },
        {
          text: '職稱',
          key: 'job_new_title',
          advanceOnly: true,
        },
        {
          text: '工號',
          key: 'employee_id',
          advanceOnly: true,
        },
        {
          text: '眷屬',
          key: 'dependents',
          advanceOnly: true,
        },
        {
          text: '其他備註',
          key: 'other_notes',
          advanceOnly: true,
        },
        {
          text: '是否為身心障礙人士',
          key: 'is_disabled',
          advanceOnly: true,
        },
      ],
    };
  },
  computed: {
    // LINE Chat ID 按鈕只在管理後台全域註冊（專業人士站的 API 也不能改 LINE Chat ID），
    // 沒註冊的站台不渲染，避免出現未知元件的空白標籤
    canEditLineChat() {
      return !!this.$options.components['line-chat-id-edit-button'];
    },
    dataAPI() {
      if (this.patientMode) {
        return getPatient;
      }
      return getUser;
    },
  },
  methods: {
    handleClose(done) {
      done();
    },
    showPersonRecord() {
      this.$emit('showProRecord', this.data.id);
      this.drawer = false;
    },
    showDetail() {
      this.$root.$emit('show-user', this.data);
      this.drawer = false;
    },
    show(data) {
      this.drawer = true;
      if (data.type === 'id') {
        if (this.data.id !== data.data) {
          this.data = {};
        }
        setTimeout(async () => {
          if (this.data.id === data.data) {
            const user = await this.dataAPI(data.data);
            this.data = user;
            return;
          }
          this.$execWithLoading(async () => {
            const user = await this.dataAPI(data.data);
            this.data = user;
          }, (e) => {
            this.data = { id: data.data };
            this.$showAxiosException('載入個案資料失敗', e);
          });
        }, 100);
      } else {
        this.data = data.data;
      }
    },
    editPerson() {
      this.$refs.personEditor.$emit('show', {
        type: 'data',
        data: this.data,
      });
    },
    updateResult(result) {
      if (result) {
        this.show({
          type: 'id',
          data: this.data.id,
        });
      }
      this.$forceUpdate();
    },
  },
  mounted() {
    this.$on('show', (data) => {
      this.show(data);
    });
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.drawer {
  padding: 0 20px;
  padding-bottom: 20px;
  .name-block {
    .name-row {
      .name {
        font-size: 20px;
      }
    }
    .status {
      font-size: 16px;
      .line-img {
        width: 16px;
        height: 16px;
        height: auto;
        background-image: url('/line.png');
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        &.invalid {
          filter: grayscale(100%);
        }
      }
    }
  }
  .seperator {
    border-bottom: 1px solid $border-base-color;
  }
  .info-block {
    overflow: auto;
    overflow: overlay;
    .info-column {
      .colname {
        font-size: 12px;
        color: $text-secondary-color;
      }
      .value {
        color: $text-regular-color;
      }
    }
  }

}
</style>
